import type { LoginMutationVariables, RegisterCustomerInput, RequestPasswordResetMutation, RequestPasswordResetMutationVariables, ResetPasswordMutation, ResetPasswordMutationVariables, VerifyCustomerAccountMutationVariables } from '#graphql-operations'
import { ErrorCode } from '#graphql-operations'
import { InvalidCredentialsError, MissingPasswordError, NativeAuthStrategyError, NotVerifiedError, PasswordAlreadySetError, PasswordResetTokenExpiredError, PasswordResetTokenInvalidError, PasswordValidationError, VerificationTokenExpiredError, VerificationTokenInvalidError } from './errors'

export async function login(body: LoginMutationVariables) {
  const { data } = await useGraphqlMutation('login', body)

  if (isGraphQLError(data.login)) {
    switch (data.login.errorCode) {
      case ErrorCode.INVALID_CREDENTIALS_ERROR: {
        throw new InvalidCredentialsError(data.login.message, data.login.authenticationError)
      }
      case ErrorCode.NATIVE_AUTH_STRATEGY_ERROR: {
        throw new NativeAuthStrategyError(data.registerCustomerAccount.message)
      }
      case ErrorCode.NOT_VERIFIED_ERROR: {
        throw new NotVerifiedError(data.login.message)
      }
      default: {
        throw new Error(data.login.message)
      }
    }
  }
}

export async function logout() {
  await useGraphqlMutation('logout')
}

export async function registerCustomerAccount(input: WithRequired<RegisterCustomerInput, 'password'>) {
  const { data } = await useGraphqlMutation('registerCustomerAccount', { input })

  if (isGraphQLError(data.registerCustomerAccount)) {
    switch (data.registerCustomerAccount.errorCode) {
      case ErrorCode.MISSING_PASSWORD_ERROR: {
        throw new MissingPasswordError(data.registerCustomerAccount.message)
      }
      case ErrorCode.NATIVE_AUTH_STRATEGY_ERROR: {
        throw new NativeAuthStrategyError(data.registerCustomerAccount.message)
      }
      case ErrorCode.PASSWORD_VALIDATION_ERROR: {
        throw new PasswordValidationError(data.registerCustomerAccount.message, data.registerCustomerAccount.validationErrorMessage)
      }
      default: {
        throw new Error(data.registerCustomerAccount.message)
      }
    }
  }
}

export async function verifyCustomerAccount(body: VerifyCustomerAccountMutationVariables) {
  const { data } = await useGraphqlMutation('verifyCustomerAccount', body)

  if (isGraphQLError(data.verifyCustomerAccount)) {
    switch (data.verifyCustomerAccount.errorCode) {
      case ErrorCode.MISSING_PASSWORD_ERROR: {
        throw new MissingPasswordError(data.verifyCustomerAccount.message)
      }
      case ErrorCode.NATIVE_AUTH_STRATEGY_ERROR: {
        throw new NativeAuthStrategyError(data.verifyCustomerAccount.message)
      }
      case ErrorCode.PASSWORD_ALREADY_SET_ERROR: {
        throw new PasswordAlreadySetError(data.verifyCustomerAccount.message)
      }
      case ErrorCode.PASSWORD_VALIDATION_ERROR: {
        throw new PasswordValidationError(data.verifyCustomerAccount.message, data.verifyCustomerAccount.validationErrorMessage)
      }
      case ErrorCode.VERIFICATION_TOKEN_EXPIRED_ERROR: {
        throw new VerificationTokenExpiredError(data.verifyCustomerAccount.message)
      }
      case ErrorCode.VERIFICATION_TOKEN_INVALID_ERROR: {
        throw new VerificationTokenInvalidError(data.verifyCustomerAccount.message)
      }
      default: {
        throw new Error(data.verifyCustomerAccount.message)
      }
    }
  }
}

export async function resetPassword(body: ResetPasswordMutationVariables) {
  const { data } = await useGraphqlMutation('resetPassword', body) as { data: ResetPasswordMutation }

  if (isGraphQLError(data.resetPassword)) {
    switch (data.resetPassword.errorCode) {
      case ErrorCode.NATIVE_AUTH_STRATEGY_ERROR: {
        throw new NativeAuthStrategyError(data.resetPassword.message)
      }
      case ErrorCode.NOT_VERIFIED_ERROR: {
        throw new NotVerifiedError(data.resetPassword.message)
      }
      case ErrorCode.PASSWORD_RESET_TOKEN_EXPIRED_ERROR: {
        throw new PasswordResetTokenExpiredError(data.resetPassword.message)
      }
      case ErrorCode.PASSWORD_RESET_TOKEN_INVALID_ERROR: {
        throw new PasswordResetTokenInvalidError(data.resetPassword.message)
      }
      case ErrorCode.PASSWORD_VALIDATION_ERROR: {
        throw new PasswordValidationError(data.resetPassword.message, data.resetPassword.validationErrorMessage)
      }
    }
  }
}

export async function requestPasswordReset(body: RequestPasswordResetMutationVariables) {
  const { data } = await useGraphqlMutation('requestPasswordReset', body) as { data: RequestPasswordResetMutation }

  if (isGraphQLError(data.requestPasswordReset)) {
    switch (data.requestPasswordReset.errorCode) {
      case ErrorCode.NATIVE_AUTH_STRATEGY_ERROR: {
        throw new NativeAuthStrategyError(data.requestPasswordReset.message)
      }
      default: {
        throw new Error(data.requestPasswordReset.message)
      }
    }
  }
}
